/**
 * Iter 253z-11 · Performance Marketing hub — dashboard landing page.
 *
 * Shows KPI cards (spend, CTR, CPC, ROAS, conversions) with WoW deltas,
 * daily-spend trend line, top-5 campaigns, and quick links to Accounts,
 * Campaigns, Reports, Alerts.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, DollarSign, MousePointerClick, Target, Zap, TrendingUp, TrendingDown, PlugZap, Activity, FileBarChart, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const fmtInt = (n) => (Number(n) || 0).toLocaleString();
const fmtMoney = (n, ccy = "USD") => `${ccy} ${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtPct = (n) => `${(Number(n) || 0).toFixed(2)}%`;

function pctDelta(cur, prev) {
  if (!prev) return null;
  return ((cur - prev) / prev) * 100;
}

function DeltaBadge({ delta, invertGood }) {
  if (delta === null || delta === undefined) return <span className="text-[10px] text-muted-foreground">—</span>;
  const good = invertGood ? delta < 0 : delta > 0;
  const Icon = delta > 0 ? TrendingUp : TrendingDown;
  const cls = good ? "text-emerald-600" : "text-rose-600";
  return (
    <span className={`text-[10px] font-semibold inline-flex items-center gap-0.5 ${cls}`}>
      <Icon className="w-2.5 h-2.5" /> {Math.abs(delta).toFixed(1)}%
    </span>
  );
}

function KpiCard({ label, value, icon: Icon, delta, invertGood, testid }) {
  return (
    <div className="border border-border p-4 bg-background" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="label-eyebrow inline-flex items-center gap-1.5">
          <Icon className="w-3 h-3" /> {label}
        </span>
        <DeltaBadge delta={delta} invertGood={invertGood} />
      </div>
      <div className="text-2xl font-bold tabular-nums" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </div>
  );
}

function TrendChart({ series }) {
  if (!series.length) return <div className="text-xs text-muted-foreground p-6 text-center">No data yet.</div>;
  const maxSpend = Math.max(...series.map((s) => s.spend), 1);
  const H = 140;
  const step = 100 / Math.max(series.length - 1, 1);
  const points = series.map((s, i) => `${i * step},${H - (s.spend / maxSpend) * (H - 20)}`).join(" ");
  return (
    <div className="border border-border bg-background p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="label-eyebrow">Daily spend trend</div>
        <div className="text-[10px] text-muted-foreground">{series[0]?.date} → {series[series.length - 1]?.date}</div>
      </div>
      <svg viewBox={`0 0 100 ${H}`} width="100%" height={H} preserveAspectRatio="none">
        <polyline points={points} fill="none" stroke="hsl(var(--primary))" strokeWidth="0.6" />
        <polygon points={`0,${H} ${points} 100,${H}`} fill="hsl(var(--primary))" fillOpacity="0.08" />
      </svg>
      <div className="flex justify-between text-[9px] text-muted-foreground tabular-nums mt-1">
        <span>0</span>
        <span>{fmtInt(Math.round(maxSpend))}</span>
      </div>
    </div>
  );
}

export default function PerformanceHub() {
  const { activeWorkspaceId } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [trend, setTrend] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [range, setRange] = useState("30");
  const [busy, setBusy] = useState(true);
  const [hasConn, setHasConn] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const dateFrom = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - Number(range) + 1);
    return d.toISOString().slice(0, 10);
  }, [range]);
  const dateTo = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const [conn, bs, k, tr, cs, an] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/perf/connections`),
        api.get(`/workspaces/${activeWorkspaceId}/brands`).catch(() => ({ data: [] })),
        api.get(`/workspaces/${activeWorkspaceId}/perf/kpis`, { params: { date_from: dateFrom, date_to: dateTo, brand_id: brandId || undefined } }),
        api.get(`/workspaces/${activeWorkspaceId}/perf/insights/trend`, { params: { date_from: dateFrom, date_to: dateTo, brand_id: brandId || undefined } }),
        api.get(`/workspaces/${activeWorkspaceId}/perf/campaigns`, { params: { brand_id: brandId || undefined } }),
        api.get(`/workspaces/${activeWorkspaceId}/perf/anomalies`),
      ]);
      setHasConn((conn.data?.rows || []).length > 0);
      setBrands(bs.data || []);
      setKpis(k.data);
      setTrend(tr.data || []);
      setCampaigns((cs.data || []).sort((a, b) => b.spend_7d - a.spend_7d).slice(0, 5));
      setAnomalies((an.data || []).filter((a) => !a.resolved_at));
    } catch (e) {
      toast.error("Failed to load performance data");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, range, brandId]);

  const kickDemoSync = async () => {
    setSyncing(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/perf/connections`, {
        mode: "demo", provider: "meta", name: "Demo Meta",
      });
      toast.success("Demo data seeded — refreshing dashboard");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to seed demo data"));
    } finally { setSyncing(false); }
  };

  const totals = kpis?.totals || {};
  const prev = kpis?.prev || {};

  return (
    <>
      <PageHeader
        eyebrow="Marketing"
        title="Performance Marketing"
        description="Central hub for Meta, Google, and other ad platforms — spend, ROAS, campaigns, and anomaly alerts across every brand."
        testid="performance-hub-header"
        actions={
          <div className="flex items-center gap-2">
            <Select value={brandId || "_all"} onValueChange={(v) => setBrandId(v === "_all" ? "" : v)}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="perf-hub-brand"><SelectValue placeholder="All brands" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All brands</SelectItem>
                {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="h-8 w-28 text-xs" data-testid="perf-hub-range"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7d</SelectItem>
                <SelectItem value="14">Last 14d</SelectItem>
                <SelectItem value="30">Last 30d</SelectItem>
                <SelectItem value="90">Last 90d</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={load} className="rounded-none h-8" data-testid="perf-hub-refresh">
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 pb-10 space-y-6">
        {/* Quick nav strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="perf-hub-quicknav">
          <QuickCard to="/app/performance/accounts" icon={PlugZap} label="Accounts" hint={hasConn ? "Connected" : "Connect Meta"} testid="perf-quick-accounts" />
          <QuickCard to="/app/performance/campaigns" icon={Activity} label="Campaigns" hint={`${campaigns.length ? campaigns.length + '+ shown' : 'Manage all'}`} testid="perf-quick-campaigns" />
          <QuickCard to="/app/performance/reports" icon={FileBarChart} label="Reports" hint="Export + schedule" testid="perf-quick-reports" />
          <QuickCard to="/app/performance/alerts" icon={AlertTriangle} label="Alerts" hint={anomalies.length ? `${anomalies.length} open` : "No anomalies"} tone={anomalies.length ? "danger" : null} testid="perf-quick-alerts" />
        </div>

        {!hasConn && (
          <div className="border border-amber-400 bg-amber-50/40 p-5 flex items-center gap-4" data-testid="perf-no-conn-banner">
            <PlugZap className="w-6 h-6 text-amber-700" />
            <div className="flex-1">
              <div className="font-bold text-sm">No ad accounts connected yet</div>
              <div className="text-xs text-muted-foreground mt-1">
                Connect a Meta ad account, or seed demo data to preview dashboards + anomaly alerts before wiring the real Meta app.
              </div>
            </div>
            <Button onClick={kickDemoSync} disabled={syncing} className="rounded-none" data-testid="perf-seed-demo-btn">
              {syncing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1.5" />}
              {syncing ? "Seeding…" : "Seed demo data"}
            </Button>
            <Link to="/app/performance/accounts">
              <Button variant="outline" className="rounded-none" data-testid="perf-connect-meta-btn">
                <PlugZap className="w-3.5 h-3.5 mr-1.5" /> Connect Meta
              </Button>
            </Link>
          </div>
        )}

        {/* KPI strip */}
        {busy ? (
          <div className="text-sm text-muted-foreground">Loading KPIs…</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="perf-hub-kpis">
            <KpiCard label="Spend" value={fmtMoney(totals.spend)} icon={DollarSign} delta={pctDelta(totals.spend, prev.spend)} testid="perf-kpi-spend" />
            <KpiCard label="Impressions" value={fmtInt(totals.impressions)} icon={Target} delta={pctDelta(totals.impressions, prev.impressions)} testid="perf-kpi-impressions" />
            <KpiCard label="Clicks" value={fmtInt(totals.clicks)} icon={MousePointerClick} delta={pctDelta(totals.clicks, prev.clicks)} testid="perf-kpi-clicks" />
            <KpiCard label="CTR" value={fmtPct(totals.ctr)} icon={TrendingUp} delta={pctDelta(totals.ctr, prev.ctr)} testid="perf-kpi-ctr" />
            <KpiCard label="CPC" value={fmtMoney(totals.cpc)} icon={DollarSign} delta={pctDelta(totals.cpc, prev.cpc)} invertGood testid="perf-kpi-cpc" />
            <KpiCard label="CPM" value={fmtMoney(totals.cpm)} icon={DollarSign} delta={pctDelta(totals.cpm, prev.cpm)} invertGood testid="perf-kpi-cpm" />
            <KpiCard label="Conversions" value={fmtInt(totals.conversions)} icon={Zap} delta={pctDelta(totals.conversions, prev.conversions)} testid="perf-kpi-conversions" />
            <KpiCard label="ROAS avg" value={campaigns.length ? "—" : "—"} icon={TrendingUp} delta={null} testid="perf-kpi-roas" />
          </div>
        )}

        <TrendChart series={trend} />

        {/* Top campaigns */}
        <section className="border border-border" data-testid="perf-hub-top-campaigns">
          <div className="px-4 py-2.5 border-b border-border bg-muted/30 flex items-center justify-between">
            <div className="label-eyebrow inline-flex items-center gap-1.5">
              <Activity className="w-3 h-3" /> Top 5 campaigns by 7-day spend
            </div>
            <Link to="/app/performance/campaigns" className="text-xs underline hover:no-underline">View all →</Link>
          </div>
          {campaigns.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">No campaigns yet.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-muted/20 text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Campaign</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Spend 7d</th>
                  <th className="text-right px-3 py-2">Clicks</th>
                  <th className="text-right px-3 py-2">CTR</th>
                  <th className="text-right px-3 py-2">Conv.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {campaigns.map((c) => (
                  <tr key={c.id} data-testid={`perf-top-campaign-${c.id}`}>
                    <td className="px-3 py-2 font-medium truncate max-w-xs">{c.name}</td>
                    <td className="px-3 py-2">
                      <span className={`chip text-[9px] ${c.status === "ACTIVE" ? "chip-approved" : ""}`}>{c.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(c.spend_7d, c.currency)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtInt(c.clicks_7d)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtPct(c.ctr_7d)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtInt(c.conversions_7d)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </>
  );
}

function QuickCard({ to, icon: Icon, label, hint, tone, testid }) {
  const toneCls = tone === "danger" ? "border-rose-400 bg-rose-50/40" : "border-border";
  return (
    <Link to={to} data-testid={testid}>
      <div className={`border ${toneCls} p-3 hover:border-primary transition`}>
        <div className="flex items-center gap-2">
          <Icon className={`w-3.5 h-3.5 ${tone === "danger" ? "text-rose-600" : "text-primary"}`} />
          <div className="label-eyebrow">{label}</div>
        </div>
        <div className="text-sm mt-1 font-semibold">{hint}</div>
      </div>
    </Link>
  );
}
